import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from "lodash";

import AssignCampaignsDropDown from "../CampaignAssignment/AssignCampaignsDropDown";
import DeleteConfirmation from "../../../SharedComponents/DeleteConfirmation";
import GroupHeader from "./GroupHeader";
import DeviceCard from "./DeviceCard";
import Update from "../Update";

import { setDisableBtn } from '../../../../redux/Slices/Global/globalSlice';
import { useDeleteDevice, useUpdateDeviceGroup, useOrderGroupMutation } from "../../../../queries";

function Group({ devicesGroup }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const updateDeviceGroup = useUpdateDeviceGroup();
    const deleteDevice = useDeleteDevice();
    const orderGroup = useOrderGroupMutation();

    const openDialog = useSelector((state) => state.global.openDialog);
    const deleteDialog = useSelector((state) => state.global.deleteDialog);
    const selectedDevice = useSelector((state) => state.device.selectedDevice);
    const [onlineTimes, setOnlineTimes] = useState({});
    const [columns, setColumns] = useState({});
    const prevDevicesGroupRef = useRef();
    useEffect(() => {
        if (typeof document === "undefined") return;
        document.body.classList.add("dragdrop-active");
        return () => {
            document.body.classList.remove("dragdrop-active");
        };
    }, []);
    const handleOnlineTimeUpdate = useCallback((deviceId, secondsOnline) => {
        setOnlineTimes(prev => ({ ...prev, [deviceId]: secondsOnline }));
    }, []);

    // Memoized selectors for better performance
    const selectedDeviceName = useMemo(() =>
        selectedDevice?.name ?? selectedDevice?.serial_name,
        [selectedDevice]
    );

    // Only update columns when devicesGroup actually changes
    // useEffect(() => {
    //     if (!_.isEqual(prevDevicesGroupRef.current, devicesGroup)) {
    //         setColumns(devicesGroup);
    //         prevDevicesGroupRef.current = devicesGroup;
    //     }
    // }, [devicesGroup]);

    useEffect(() => {
        if (!_.isEqual(prevDevicesGroupRef.current, devicesGroup)) {
            const sortedGroups = {};

            Object.entries(devicesGroup).forEach(([groupId, group]) => {
                const sortedItems = [...group.items].sort((a, b) => {
                    const aOnline = onlineTimes[a.id] || 0;
                    const bOnline = onlineTimes[b.id] || 0;
                    return bOnline - aOnline; // descending
                });

                sortedGroups[groupId] = { ...group, items: sortedItems };
            });

            setColumns(sortedGroups);
            prevDevicesGroupRef.current = devicesGroup;
        }
    }, [devicesGroup, onlineTimes]);

    // Memoized handler functions
    const updateDeviceGroupHandler = useCallback(async (groupID, deviceID) => {
        await updateDeviceGroup.mutateAsync({
            data: {
                "group_id": groupID === "undefined" ? "" : groupID,
                "device_id": deviceID
            }
        });
    }, [updateDeviceGroup]);

    // Group-level reorder (desktop drag handle + the ⋮ menu's "move up/down")
    // both funnel through this — the same `PUT /groups/order` mutation the
    // retired GroupsOrderingDialog used, just triggered inline instead of
    // from a separate modal.
    const moveGroup = useCallback((groupId, direction) => {
        setColumns(prevColumns => {
            const entries = Object.entries(prevColumns);
            const idx = entries.findIndex(([, g]) => g.group_id === groupId);
            if (idx === -1) return prevColumns;
            const swapWith = direction === 'up' ? idx - 1 : idx + 1;
            if (swapWith < 0 || swapWith >= entries.length) return prevColumns;
            [entries[idx], entries[swapWith]] = [entries[swapWith], entries[idx]];
            orderGroup.mutateAsync({ ids: entries.map(([, g]) => g.group_id) });
            return Object.fromEntries(entries);
        });
    }, [orderGroup]);

    const onDragEnd = useCallback((result) => {
        if (!result.destination) return;
        const { source, destination, type } = result;

        if (type === 'GROUP') {
            setColumns(prevColumns => {
                const entries = Object.entries(prevColumns);
                const [moved] = entries.splice(source.index, 1);
                entries.splice(destination.index, 0, moved);
                orderGroup.mutateAsync({ ids: entries.map(([, g]) => g.group_id) });
                return Object.fromEntries(entries);
            });
            return;
        }

        setColumns(prevColumns => {
            if (source.droppableId !== destination.droppableId) {
                const sourceColumn = prevColumns[source.droppableId];
                const destColumn = prevColumns[destination.droppableId];
                const sourceItems = [...sourceColumn.items];
                const destItems = [...destColumn.items];
                const [removed] = sourceItems.splice(source.index, 1);
                destItems.splice(destination.index, 0, removed);

                // Async update after state change. The droppableId is the prefixed
                // object key (e.g. "g1"); send the underlying group_id to the backend.
                updateDeviceGroupHandler(destColumn?.group_id ?? destination.droppableId, result.draggableId);

                return {
                    ...prevColumns,
                    [source.droppableId]: { ...sourceColumn, items: sourceItems },
                    [destination.droppableId]: { ...destColumn, items: destItems },
                };
            } else {
                const column = prevColumns[source.droppableId];
                const copiedItems = [...column.items];
                const [removed] = copiedItems.splice(source.index, 1);
                copiedItems.splice(destination.index, 0, removed);
                return {
                    ...prevColumns,
                    [source.droppableId]: { ...column, items: copiedItems },
                };
            }
        });
    }, [updateDeviceGroupHandler, orderGroup]);

    // "Assign Schedule" used to open the old (broken) scheduling popup — now a
    // shortcut straight to the new calendar with this group pre-selected.
    const assignSchedule = useCallback((groupInfo) => {
        const groupID = groupInfo?.group_id;
        navigate(groupID && groupID !== "undefined" ? `/schedule/themes?group=${groupID}` : '/schedule/themes');
    }, [navigate]);

    const deleteDeviceHandler = useCallback(async () => {
        dispatch(setDisableBtn(true));
        await deleteDevice.mutateAsync({ id: selectedDevice?.id });
    }, [deleteDevice, dispatch, selectedDevice?.id]);

    // Each group is itself a Draggable (type="GROUP", the outer Droppable
    // below) so the header's grip handle can desktop-drag-reorder groups;
    // its own device grid is a nested Droppable (type="DEVICE", unchanged
    // logic) — @hello-pangea/dnd supports this "draggable columns containing
    // their own droppable" nesting natively, distinguished by `type` so a
    // group-drag can never land in a device list or vice versa.
    const renderDroppableColumn = useCallback(([columnId, column], index) => (
        <Draggable draggableId={columnId} index={index} key={columnId}>
            {(providedGroup) => (
                <div
                    className="pp-scr-group"
                    ref={providedGroup.innerRef}
                    {...providedGroup.draggableProps}
                >
                    <GroupHeader
                        groupInfo={column}
                        assignSchedule={assignSchedule}
                        onMoveGroup={moveGroup}
                        isFirst={index === 0}
                        isLast={index === Object.keys(columns).length - 1}
                        dragHandleProps={providedGroup.dragHandleProps}
                    />
                    <Droppable droppableId={columnId} type="DEVICE" key={columnId}>
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="pp-scr-grid"
                            >
                                {column.items.length !== 0 ? (
                                    column.items.map((device, subIndex) => (
                                        <Draggable key={device.id} draggableId={device.id} index={subIndex}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        userSelect: "none",
                                                        ...provided.draggableProps.style,
                                                    }}
                                                >
                                                    <DeviceCard
                                                        normal={index < 1 ? false : true}
                                                        above={index == 0 && subIndex > 3 ? true : false}
                                                        data={device}
                                                        onOnlineTimeUpdate={handleOnlineTimeUpdate} // 🔧 HIGHLIGHT
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))
                                ) : (
                                    <div className="pp-scr-statecard" style={{ gridColumn: '1 / -1' }}>{t('devices.group_no_devices_in_group')}</div>
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            )}
        </Draggable>
    ), [assignSchedule, handleOnlineTimeUpdate, moveGroup, columns, t]);


    const renderDialogs = useMemo(() => (
        <>
            {deleteDialog.device && (
                <DeleteConfirmation
                    deleteHandler={deleteDeviceHandler}
                    itemName={selectedDeviceName}
                    dialogKey="device"
                />
            )}
            {openDialog.assignCampaignsDropDown && <AssignCampaignsDropDown deviceID={selectedDevice?.id} />}

            {openDialog.updateDevice && <Update device={selectedDevice} />}
        </>
    ), [deleteDialog.device, deleteDeviceHandler, openDialog, selectedDevice, selectedDeviceName]);

    if (_.isEmpty(columns)) return null;

    return (
        <div className="d-flex flex-column justify-content-center w-100">
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="groups-root" type="GROUP">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {Object.entries(columns).map(renderDroppableColumn)}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {renderDialogs}
        </div>
    );
}

export default React.memo(Group);
